<script setup lang="ts">
    import { computed, ref, watch, watchEffect } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import EchoButton from '@/js/components/EchoButton.vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import TextInput from '@/js/components/TextInput.vue';
    import TextareaInput from '@/js/components/TextareaInput.vue';
    import SelectInput from '@/js/components/SelectInput.vue';
    import CheckboxInput from '@/js/components/CheckboxInput.vue';
    import Accordion from '@/js/components/Accordion.vue';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import { useToastStore } from '@/js/stores/snackbar';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import {
        useQueryGetBackOfficeTenants,
        useQueryGetStreamingProviderOptions,
        useQueryGetStreamingConfigs,
        useQueryGetStreamingConfig,
        useQueryGetStreamingTenantFilterOptions,
        useMutationCreateStreamingConfig,
        useMutationUpdateStreamingConfig,
        useMutationDeleteStreamingConfig,
    } from '@/js/services/api/backOffice/backOfficeQueries';
    import {
        getV1StreamingConfig,
        type StreamingFieldDefinition,
        type StreamingRuleValue,
    } from '@/js/services/api/backOffice/streamingApi';

    const successDialogStore = useSuccessDialog();
    const toastStore = useToastStore();
    const confirmationDialogStore = useConfirmationDialog();

    const { data: tenantsData } = useQueryGetBackOfficeTenants();
    const { data: providerOptionsData } = useQueryGetStreamingProviderOptions();

    type Mode = 'list' | 'create' | 'edit';

    const selectedTenantId = ref('');
    const tenantSearch = ref<string | null>(null);
    const mode = ref<Mode>('list');

    // Form state
    const selectedProviderKey = ref('');
    const providerSearch = ref<string | null>(null);
    const editingProviderId = ref('');
    const editingEndpoint = ref('');
    const configName = ref('');
    const formValues = ref<Record<string, string>>({});
    const fieldHints = ref<Record<string, string>>({});
    const selectedSkillIds = ref<string[]>([]);
    const selectedQueueIds = ref<string[]>([]);
    const selectedChannelIds = ref<string[]>([]);
    const maxStreamingDays = ref<number | null>(null);
    const v1ImportPending = ref(false);

    const { data: streamingConfigsData } = useQueryGetStreamingConfigs(selectedTenantId);
    const { data: filterOptionsData } = useQueryGetStreamingTenantFilterOptions(selectedTenantId);

    const tenantOptions = computed<Option[]>(() =>
        (tenantsData.value ?? []).map((t) => ({ name: t.name, value: t.id }))
    );

    const providerOptions = computed<Option[]>(() =>
        (providerOptionsData.value ?? []).map((p) => ({ name: p.name, value: p.key }))
    );

    const selectedProvider = computed(
        () =>
            (providerOptionsData.value ?? []).find((p) => p.key === selectedProviderKey.value) ??
            null
    );

    const selectedProviderEndpoint = computed(
        () => editingEndpoint.value || selectedProvider.value?.endpoint || ''
    );
    const { data: existingConfigData } = useQueryGetStreamingConfig(
        selectedTenantId,
        selectedProviderEndpoint,
        editingProviderId
    );

    const skillsDisabled = computed(() => selectedQueueIds.value.length > 0);
    const queuesDisabled = computed(() => selectedSkillIds.value.length > 0);

    const toggleSkill = (id: string, checked: boolean) => {
        selectedSkillIds.value = checked
            ? [...selectedSkillIds.value, id]
            : selectedSkillIds.value.filter((s) => s !== id);
    };
    const toggleQueue = (id: string, checked: boolean) => {
        selectedQueueIds.value = checked
            ? [...selectedQueueIds.value, id]
            : selectedQueueIds.value.filter((q) => q !== id);
    };
    const toggleChannel = (id: string, checked: boolean) => {
        selectedChannelIds.value = checked
            ? [...selectedChannelIds.value, id]
            : selectedChannelIds.value.filter((c) => c !== id);
    };

    const onTenantSelect = (option: Option) => {
        selectedTenantId.value = option.value;
    };
    const onTenantClear = () => {
        selectedTenantId.value = '';
    };
    const onProviderSelect = (option: Option) => {
        selectedProviderKey.value = option.value;
    };
    const onProviderClear = () => {
        selectedProviderKey.value = '';
    };

    const clearForm = () => {
        selectedProviderKey.value = '';
        providerSearch.value = null;
        editingProviderId.value = '';
        editingEndpoint.value = '';
        configName.value = '';
        formValues.value = {};
        fieldHints.value = {};
        selectedSkillIds.value = [];
        selectedQueueIds.value = [];
        selectedChannelIds.value = [];
        maxStreamingDays.value = null;
    };

    watch(selectedTenantId, () => {
        mode.value = 'list';
        clearForm();
    });

    watchEffect(() => {
        const provider = selectedProvider.value;
        const config = existingConfigData.value;

        if (!provider) {
            formValues.value = {};
            selectedSkillIds.value = [];
            selectedQueueIds.value = [];
            selectedChannelIds.value = [];
            return;
        }

        if (config) {
            configName.value = config.name;
            formValues.value = { ...config.fieldValues };
            const rules = (config.streamingRules ?? []).filter(
                (r) => r.skillId !== null || r.queueId !== null || r.channelId !== null
            );
            selectedSkillIds.value = [
                ...new Set(rules.filter((r) => r.skillId).map((r) => r.skillId!)),
            ];
            selectedQueueIds.value = [
                ...new Set(rules.filter((r) => r.queueId).map((r) => r.queueId!)),
            ];
            selectedChannelIds.value = [
                ...new Set(rules.filter((r) => r.channelId).map((r) => r.channelId!)),
            ];
            maxStreamingDays.value = config.streamingRules?.[0]?.maxStreamingDays ?? null;
        } else if (mode.value === 'create') {
            formValues.value = Object.fromEntries(
                provider.fields.map((f) => [f.key, f.defaultValue ?? ''])
            );
            selectedSkillIds.value = [];
            selectedQueueIds.value = [];
            selectedChannelIds.value = [];
        }
    });

    const importFromV1 = async (endpoint: string) => {
        if (!selectedTenantId.value || !selectedProvider.value) return;
        v1ImportPending.value = true;
        try {
            const config = await getV1StreamingConfig(selectedTenantId.value, endpoint);
            if (!config) {
                toastStore.addToast('info', __('back-office-streaming-config-v1-not-found'));
                return;
            }
            for (const field of selectedProvider.value.fields) {
                if (!field.canImportFromV1) continue;
                const value = config[field.key as keyof typeof config];
                if (value !== null && value !== undefined) {
                    formValues.value[field.key] = String(value);
                }
            }
            fieldHints.value = config.fieldDescriptions ?? {};
            if (config.rules?.length) {
                selectedSkillIds.value = [
                    ...new Set(config.rules.filter((r) => r.skillId).map((r) => r.skillId!)),
                ];
                selectedQueueIds.value = [
                    ...new Set(config.rules.filter((r) => r.queueId).map((r) => r.queueId!)),
                ];
                selectedChannelIds.value = [
                    ...new Set(config.rules.filter((r) => r.channelId).map((r) => r.channelId!)),
                ];
            }
        } finally {
            v1ImportPending.value = false;
        }
    };

    const resetForm = () => {
        const config = existingConfigData.value;
        if (config) {
            configName.value = config.name;
            formValues.value = { ...config.fieldValues };
            const rules = (config.streamingRules ?? []).filter(
                (r) => r.skillId !== null || r.queueId !== null || r.channelId !== null
            );
            selectedSkillIds.value = [
                ...new Set(rules.filter((r) => r.skillId).map((r) => r.skillId!)),
            ];
            selectedQueueIds.value = [
                ...new Set(rules.filter((r) => r.queueId).map((r) => r.queueId!)),
            ];
            selectedChannelIds.value = [
                ...new Set(rules.filter((r) => r.channelId).map((r) => r.channelId!)),
            ];
            maxStreamingDays.value = config.streamingRules?.[0]?.maxStreamingDays ?? null;
        } else if (selectedProvider.value) {
            configName.value = '';
            formValues.value = Object.fromEntries(
                selectedProvider.value.fields.map((f) => [f.key, f.defaultValue ?? ''])
            );
            selectedSkillIds.value = [];
            selectedQueueIds.value = [];
            selectedChannelIds.value = [];
            maxStreamingDays.value = null;
        }
    };

    const startCreate = () => {
        clearForm();
        mode.value = 'create';
    };

    const startEdit = (providerId: string, providerKey: string, endpoint: string) => {
        clearForm();
        editingProviderId.value = providerId;
        editingEndpoint.value = endpoint;
        selectedProviderKey.value = providerKey;
        providerSearch.value =
            (providerOptionsData.value ?? []).find((p) => p.key === providerKey)?.name ??
            providerKey;
        mode.value = 'edit';
    };

    const cancelForm = () => {
        clearForm();
        mode.value = 'list';
    };

    const { mutateAsync: createMutation, isPending: isCreating } =
        useMutationCreateStreamingConfig();
    const { mutateAsync: updateMutation, isPending: isUpdating } =
        useMutationUpdateStreamingConfig();
    const { mutateAsync: deleteMutation } = useMutationDeleteStreamingConfig();

    const isPending = computed(() => isCreating.value || isUpdating.value);

    // Unknown tokens are saved anyway (may be intentional literal text), only flagged
    const unknownTemplateVariables = (field: StreamingFieldDefinition): string[] => {
        if (!field.templateVariables?.length) return [];
        const known = new Set(field.templateVariables.map((v) => v.token));
        const tokens = (formValues.value[field.key] ?? '').match(/\{[^{}]*\}/g) ?? [];
        return [...new Set(tokens.filter((t) => !known.has(t)))];
    };

    const deletingProviderId = ref<string | null>(null);

    const deleteConfig = async (providerId: string) => {
        const confirmed = await confirmationDialogStore.openDialog({
            title: __('delete'),
            message: __('back-office-streaming-config-delete-confirm'),
        });
        if (!confirmed) return;

        deletingProviderId.value = providerId;
        try {
            await deleteMutation({ providerId, tenantId: selectedTenantId.value });
            cancelForm();
        } finally {
            deletingProviderId.value = null;
        }
    };

    const streamingRulesForSubmit = computed((): StreamingRuleValue[] => {
        const primary =
            selectedSkillIds.value.length > 0
                ? selectedSkillIds.value.map((skillId) => ({ skillId, queueId: null }))
                : selectedQueueIds.value.length > 0
                  ? selectedQueueIds.value.map((queueId) => ({ skillId: null, queueId }))
                  : [{ skillId: null, queueId: null }];

        const channels = selectedChannelIds.value.length > 0 ? selectedChannelIds.value : [null];
        const days = maxStreamingDays.value;

        return primary.flatMap(({ skillId, queueId }) =>
            channels.map((channelId) => ({
                skillId,
                queueId,
                channelId,
                isEnabled: true,
                maxStreamingDays: days,
            }))
        );
    });

    const canSubmit = computed(() => {
        if (!selectedTenantId.value || !selectedProvider.value || !configName.value.trim())
            return false;

        const fieldsOk = selectedProvider.value.fields
            .filter((f) => f.required)
            .every((f) => {
                if (f.type === 'password' && mode.value === 'edit') return true;
                return !!formValues.value[f.key];
            });
        if (!fieldsOk) return false;

        if (selectedProviderKey.value === 'Sftp' && mode.value === 'create') {
            return !!(formValues.value.password || formValues.value.privateKey);
        }
        return true;
    });

    const submit = async () => {
        if (!canSubmit.value || !selectedProvider.value) return;
        const rules = streamingRulesForSubmit.value;

        const fieldData = { ...formValues.value };
        for (const field of selectedProvider.value.fields) {
            if ((field.type === 'password' || field.type === 'textarea') && !fieldData[field.key]) {
                delete fieldData[field.key];
            }
        }

        if (mode.value === 'edit' && editingProviderId.value) {
            await updateMutation({
                endpoint: selectedProviderEndpoint.value,
                providerId: editingProviderId.value,
                tenantId: selectedTenantId.value,
                data: { name: configName.value, ...fieldData, streamingRules: rules },
            });
        } else {
            await createMutation({
                endpoint: selectedProviderEndpoint.value,
                tenantId: selectedTenantId.value,
                data: {
                    tenantId: selectedTenantId.value,
                    name: configName.value,
                    ...fieldData,
                    streamingRules: rules,
                },
            });
        }

        successDialogStore.openDialog({ message: __('back-office-streaming-config-success') });
        cancelForm();
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-streaming-config') }}</h1>
        </template>
        <template #content>
            <div class="w-lg max-w-full flex flex-col gap-6">
                <AutoComplete
                    v-model="tenantSearch"
                    input-id="streaming-config-tenant"
                    :label="__('back-office-select-customer')"
                    :options="tenantOptions"
                    @select="onTenantSelect"
                    @clear="onTenantClear"
                />

                <template v-if="selectedTenantId">
                    <!-- Listvy -->
                    <template v-if="mode === 'list'">
                        <div class="flex flex-col gap-3">
                            <div
                                v-for="config in streamingConfigsData ?? []"
                                :key="config.providerId"
                                class="flex items-center justify-between rounded-md border border-gray-light px-4 py-3"
                            >
                                <div>
                                    <p class="text-body-bold">{{ config.name }}</p>
                                    <p
                                        v-if="config.configSummary"
                                        class="text-body text-gray-medium whitespace-pre-line"
                                    >
                                        {{ config.configSummary }}
                                    </p>
                                </div>
                                <div class="flex gap-2">
                                    <EchoButton
                                        type="button"
                                        variant="secondary"
                                        @click="
                                            startEdit(
                                                config.providerId,
                                                config.providerKey,
                                                config.endpoint
                                            )
                                        "
                                    >
                                        {{ __('edit') }}
                                    </EchoButton>
                                    <EchoButton
                                        type="button"
                                        variant="secondary"
                                        :loading="deletingProviderId === config.providerId"
                                        @click="deleteConfig(config.providerId)"
                                    >
                                        {{ __('delete') }}
                                    </EchoButton>
                                </div>
                            </div>
                            <p
                                v-if="streamingConfigsData?.length === 0"
                                class="text-body text-gray-medium"
                            >
                                {{ __('back-office-streaming-config-none') }}
                            </p>
                        </div>
                        <div>
                            <EchoButton type="button" variant="primary" @click="startCreate">
                                {{ __('back-office-streaming-config-add') }}
                            </EchoButton>
                        </div>
                    </template>

                    <!-- Formulärvy (create / edit) -->
                    <template v-else>
                        <form class="flex flex-col gap-6" @submit.prevent="submit">
                            <FormGroup>
                                <div class="flex flex-col gap-1">
                                    <label for="streaming-config-name" class="text-overline">
                                        {{ __('back-office-streaming-config-name') }}
                                        <span aria-hidden="true">*</span>
                                    </label>
                                    <TextInput id="streaming-config-name" v-model="configName" />
                                </div>

                                <AutoComplete
                                    v-model="providerSearch"
                                    input-id="streaming-config-provider"
                                    :label="__('back-office-streaming-config-provider')"
                                    :options="providerOptions"
                                    :disabled="mode === 'edit'"
                                    @select="onProviderSelect"
                                    @clear="onProviderClear"
                                />

                                <template v-if="selectedProvider">
                                    <template v-if="selectedProvider.v1Integrations?.length">
                                        <EchoButton
                                            v-for="integration in selectedProvider.v1Integrations"
                                            :key="integration.endpoint"
                                            type="button"
                                            variant="secondary"
                                            :loading="v1ImportPending"
                                            @click="importFromV1(integration.endpoint)"
                                        >
                                            {{ __('back-office-streaming-config-import-from-v1') }}:
                                            {{ integration.name }}
                                        </EchoButton>
                                    </template>

                                    <div
                                        v-for="field in selectedProvider.fields"
                                        :key="field.key"
                                        class="flex flex-col gap-1"
                                    >
                                        <label
                                            :for="`streaming-field-${field.key}`"
                                            class="text-overline"
                                        >
                                            {{ field.label }}
                                            <span v-if="field.required" aria-hidden="true">*</span>
                                        </label>
                                        <SelectInput
                                            v-if="field.type === 'select'"
                                            :id="`streaming-field-${field.key}`"
                                            v-model="formValues[field.key]"
                                        >
                                            <option value="" disabled>–</option>
                                            <option
                                                v-for="opt in field.options ?? []"
                                                :key="opt"
                                                :value="opt"
                                            >
                                                {{ opt }}
                                            </option>
                                        </SelectInput>
                                        <TextInput
                                            v-else-if="field.type === 'password'"
                                            :id="`streaming-field-${field.key}`"
                                            v-model="formValues[field.key]"
                                            type="password"
                                            :placeholder="
                                                existingConfigData?.storedSecrets?.includes(
                                                    field.key
                                                )
                                                    ? '••••••••'
                                                    : ''
                                            "
                                        />
                                        <TextareaInput
                                            v-else-if="field.type === 'textarea'"
                                            :id="`streaming-field-${field.key}`"
                                            v-model="formValues[field.key]"
                                            :placeholder="
                                                existingConfigData?.storedSecrets?.includes(
                                                    field.key
                                                )
                                                    ? '(privat nyckel lagrad)'
                                                    : ''
                                            "
                                        />
                                        <TextInput
                                            v-else
                                            :id="`streaming-field-${field.key}`"
                                            v-model="formValues[field.key]"
                                        />
                                        <p
                                            v-if="fieldHints[field.key]"
                                            class="text-body text-gray-medium"
                                        >
                                            {{ fieldHints[field.key] }}
                                        </p>
                                        <p
                                            v-if="field.helpText"
                                            class="text-body text-gray-medium whitespace-pre-line break-words"
                                        >
                                            {{ field.helpText }}
                                        </p>
                                        <div
                                            v-if="field.templateVariables?.length"
                                            class="text-body text-gray-medium"
                                        >
                                            <p>
                                                {{
                                                    __(
                                                        'back-office-streaming-config-template-variables'
                                                    )
                                                }}
                                            </p>
                                            <ul class="list-disc pl-5">
                                                <li
                                                    v-for="variable in field.templateVariables"
                                                    :key="variable.token"
                                                >
                                                    <code>{{ variable.token }}</code>
                                                    – {{ variable.description }}
                                                </li>
                                            </ul>
                                        </div>
                                        <p
                                            v-if="unknownTemplateVariables(field).length"
                                            class="text-body text-error"
                                            role="status"
                                        >
                                            {{
                                                __(
                                                    'back-office-streaming-config-unknown-variables',
                                                    {
                                                        variables:
                                                            unknownTemplateVariables(field).join(
                                                                ', '
                                                            ),
                                                    }
                                                )
                                            }}
                                        </p>
                                    </div>

                                    <div class="flex flex-col gap-1">
                                        <label
                                            for="streaming-max-streaming-days"
                                            class="text-overline"
                                        >
                                            {{
                                                __(
                                                    'back-office-streaming-config-max-streaming-days'
                                                )
                                            }}
                                        </label>
                                        <TextInput
                                            id="streaming-max-streaming-days"
                                            type="number"
                                            :model-value="
                                                maxStreamingDays !== null
                                                    ? String(maxStreamingDays)
                                                    : ''
                                            "
                                            @update:model-value="
                                                maxStreamingDays = $event ? Number($event) : null
                                            "
                                        />
                                    </div>

                                    <fieldset class="mt-2">
                                        <legend class="text-overline mb-3 block">
                                            {{ __('back-office-streaming-config-rules') }}
                                        </legend>
                                        <div class="flex flex-col gap-4">
                                            <Accordion>
                                                <template #summary>
                                                    <span class="flex-1">
                                                        <span class="text-overline">
                                                            {{
                                                                __(
                                                                    'back-office-streaming-config-rule-skill'
                                                                )
                                                            }}
                                                        </span>
                                                        <span
                                                            class="ml-3 text-body text-gray-medium"
                                                        >
                                                            <template
                                                                v-if="selectedSkillIds.length === 0"
                                                            >
                                                                {{
                                                                    __(
                                                                        'back-office-streaming-config-none-selected-skills'
                                                                    )
                                                                }}
                                                            </template>
                                                            <template v-else>
                                                                ({{ selectedSkillIds.length }})
                                                            </template>
                                                        </span>
                                                    </span>
                                                </template>
                                                <div class="flex flex-col gap-1 px-5 pb-3">
                                                    <CheckboxInput
                                                        v-for="skill in filterOptionsData?.skills ??
                                                        []"
                                                        :id="`streaming-skill-${skill.id}`"
                                                        :key="skill.id"
                                                        :model-value="
                                                            selectedSkillIds.includes(skill.id)
                                                        "
                                                        :disabled="skillsDisabled"
                                                        @update:model-value="
                                                            toggleSkill(skill.id, $event ?? false)
                                                        "
                                                    >
                                                        {{ skill.name }}
                                                    </CheckboxInput>
                                                </div>
                                            </Accordion>

                                            <Accordion>
                                                <template #summary>
                                                    <span class="flex-1">
                                                        <span class="text-overline">
                                                            {{
                                                                __(
                                                                    'back-office-streaming-config-rule-queue'
                                                                )
                                                            }}
                                                        </span>
                                                        <span
                                                            class="ml-3 text-body text-gray-medium"
                                                        >
                                                            <template
                                                                v-if="selectedQueueIds.length === 0"
                                                            >
                                                                {{
                                                                    __(
                                                                        'back-office-streaming-config-none-selected-queues'
                                                                    )
                                                                }}
                                                            </template>
                                                            <template v-else>
                                                                ({{ selectedQueueIds.length }})
                                                            </template>
                                                        </span>
                                                    </span>
                                                </template>
                                                <div class="flex flex-col gap-1 px-5 pb-3">
                                                    <CheckboxInput
                                                        v-for="queue in filterOptionsData?.queues ??
                                                        []"
                                                        :id="`streaming-queue-${queue.id}`"
                                                        :key="queue.id"
                                                        :model-value="
                                                            selectedQueueIds.includes(queue.id)
                                                        "
                                                        :disabled="queuesDisabled"
                                                        @update:model-value="
                                                            toggleQueue(queue.id, $event ?? false)
                                                        "
                                                    >
                                                        {{ queue.name }}
                                                    </CheckboxInput>
                                                </div>
                                            </Accordion>

                                            <Accordion>
                                                <template #summary>
                                                    <span class="flex-1">
                                                        <span class="text-overline">
                                                            {{
                                                                __(
                                                                    'back-office-streaming-config-rule-channel'
                                                                )
                                                            }}
                                                        </span>
                                                        <span
                                                            class="ml-3 text-body text-gray-medium"
                                                        >
                                                            <template
                                                                v-if="
                                                                    selectedChannelIds.length === 0
                                                                "
                                                            >
                                                                {{
                                                                    __(
                                                                        'back-office-streaming-config-none-selected-channels'
                                                                    )
                                                                }}
                                                            </template>
                                                            <template v-else>
                                                                ({{ selectedChannelIds.length }})
                                                            </template>
                                                        </span>
                                                    </span>
                                                </template>
                                                <div class="flex flex-col gap-1 px-5 pb-3">
                                                    <CheckboxInput
                                                        v-for="channel in filterOptionsData?.channels ??
                                                        []"
                                                        :id="`streaming-channel-${channel.id}`"
                                                        :key="channel.id"
                                                        :model-value="
                                                            selectedChannelIds.includes(channel.id)
                                                        "
                                                        @update:model-value="
                                                            toggleChannel(
                                                                channel.id,
                                                                $event ?? false
                                                            )
                                                        "
                                                    >
                                                        {{ channel.name }}
                                                    </CheckboxInput>
                                                </div>
                                            </Accordion>
                                        </div>
                                    </fieldset>
                                </template>
                            </FormGroup>

                            <div class="flex justify-between">
                                <EchoButton
                                    v-if="mode === 'edit'"
                                    type="button"
                                    variant="secondary"
                                    :loading="deletingProviderId === editingProviderId"
                                    @click="deleteConfig(editingProviderId)"
                                >
                                    {{ __('delete') }}
                                </EchoButton>
                                <div class="flex gap-3">
                                    <EchoButton
                                        type="submit"
                                        variant="primary"
                                        :disabled="!canSubmit"
                                        :loading="isPending"
                                    >
                                        {{ __('back-office-streaming-config-save') }}
                                    </EchoButton>
                                    <EchoButton
                                        type="button"
                                        variant="secondary"
                                        @click="resetForm"
                                    >
                                        {{ __('back-office-streaming-config-reset') }}
                                    </EchoButton>
                                    <EchoButton
                                        type="button"
                                        variant="secondary"
                                        @click="cancelForm"
                                    >
                                        {{ __('cancel') }}
                                    </EchoButton>
                                </div>
                            </div>
                        </form>
                    </template>
                </template>
            </div>
        </template>
    </SettingsPageLayout>
</template>
